import { Shell } from "@/components/layout/Shell";
import { useAuth } from "@/hooks/use-auth";
import { StatCard } from "@/components/ui/stat-card";
import { cn } from "@/lib/utils";
import {
  FileText,
  Building2,
  CheckCircle,
  Clock,
} from "lucide-react";
import {
  useRemontaLaudos,
  useEmpresas,
  useUsuarios,
} from "@/hooks/use-data";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LAUDO_STATUS_LABELS, LAUDO_STATUS_COLORS } from "@/lib/constants";
import { useLocation } from "wouter";

export default function Dashboard() {
  const { profile } = useAuth();
  const [, navigate] = useLocation();
  const empresaId = profile?.empresa?.id;

  const { data: laudos, isLoading: laudosLoading } = useRemontaLaudos();
  const { data: empresas } = useEmpresas();
  const { data: usuarios } = useUsuarios(empresaId);

  const recentLaudos = laudos?.slice(0, 5) || [];

  const stats = laudos
    ? {
        total: laudos.length,
        rascunho: laudos.filter((l: any) => l.status === "rascunho").length,
        em_vistoria: laudos.filter((l: any) => l.status === "em_vistoria").length,
        em_avaliacao_rt: laudos.filter((l: any) => l.status === "em_avaliacao_rt").length,
        finalizado: laudos.filter((l: any) => l.status === "finalizado").length,
      }
    : null;
  const statsLoading = laudosLoading;

  return (
    <Shell>
      <div className="mb-8">
        <h1
          className="text-3xl font-bold font-display text-slate-900"
          data-testid="text-page-title"
        >
          Painel de Controle
        </h1>
        <p className="text-muted-foreground mt-1">
          Bem-vindo de volta, {profile?.nome}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        {statsLoading ? (
          Array(4)
            .fill(0)
            .map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)
        ) : (
          <>
            <StatCard
              title="Total de Reclassificações"
              value={stats?.total || 0}
              icon={FileText}
              iconClassName="bg-blue-500/10 text-blue-600"
            />
            <StatCard
              title="Reclassificações Finalizadas"
              value={stats?.finalizado || 0}
              icon={CheckCircle}
              iconClassName="bg-emerald-500/10 text-emerald-600"
            />
            <StatCard
              title="Reclassificações Pendentes"
              value={
                (stats?.rascunho || 0) +
                (stats?.em_vistoria || 0) +
                (stats?.em_avaliacao_rt || 0)
              }
              icon={Clock}
              iconClassName="bg-amber-500/10 text-amber-600"
            />
            <StatCard
              title="Solicitantes"
              value={
                empresas?.filter((e) => e.tipo === "PARCEIRO_DE_NEGOCIOS")
                  .length || 0
              }
              icon={Building2}
              iconClassName="bg-purple-500/10 text-purple-600"
            />
          </>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-7">
        <Card className="lg:col-span-4 border-none shadow-lg shadow-slate-200/50">
          <CardHeader>
            <CardTitle>Resumo por Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statsLoading
                ? Array(6)
                    .fill(0)
                    .map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
                : Object.entries(LAUDO_STATUS_LABELS).map(([key, label]) => {
                    const statusKey = key as keyof NonNullable<typeof stats>;
                    return (
                    <div
                      key={key}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                    >
                      <span className="text-sm font-medium text-slate-700">
                        {label}
                      </span>
                      <span
                        className={cn(
                          "px-3 py-1 rounded-full text-xs font-bold",
                          LAUDO_STATUS_COLORS[key],
                        )}
                      >
                        {stats?.[statusKey] ?? 0}
                      </span>
                    </div>
                    );
                  })}
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-none shadow-lg shadow-slate-200/50">
          <CardHeader>
            <CardTitle>Reclassificações Recentes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {laudosLoading ? (
                Array(3)
                  .fill(0)
                  .map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
              ) : recentLaudos.length > 0 ? (
                recentLaudos.map((laudo) => (
                  <div
                    key={laudo.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => navigate(`/laudos/${laudo.id}`)}
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {laudo.codigo || "Sem código"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {laudo.veiculo
                          ? `${laudo.veiculo.marca} ${laudo.veiculo.modelo} — ${laudo.veiculo.placa}`
                          : "Sem veículo"}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                        LAUDO_STATUS_COLORS[laudo.status] ||
                          "bg-slate-100 text-slate-700",
                      )}
                    >
                      {LAUDO_STATUS_LABELS[laudo.status] || laudo.status}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  Nenhuma reclassificação encontrada
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Shell>
  );
}
